import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { CartItemsComponent } from '@theme/component-cart-items';
/** Keeps the draft's native cart route at five total units. Checkout server rules are separate. */
class AnnaOrderLimit extends Component {
  requiredRefs = ['count', 'message'];
  updateCount(count) {
    this.dataset.count = String(count);
    const excess = count - 5;
    this.toggleAttribute('data-over', excess > 0);
    if (this.refs.edit) this.refs.edit.hidden = excess <= 0;
    this.refs.count.textContent = this.dataset.countCopy.replace('[count]', String(count));
    this.refs.message.textContent = excess > 1 ? this.dataset.overMany.replace('[count]', String(excess)) : excess === 1 ? this.dataset.overOne : count === 5 ? this.dataset.fullCopy : count >= 3 ? this.dataset.qualifiedCopy : count === 2 ? this.dataset.addOne : this.dataset.addMany.replace('[count]', String(3 - count));
  }
  editBag() {
    const surface = this.closest('dialog, .cart-page');
    const quantity = surface?.querySelector('input[name="updates[]"], quantity-selector input');
    quantity?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    quantity?.focus({ preventScroll: true });
  }
  showError() { this.refs.message.textContent = this.dataset.checkFailed; }
}
class AnnaOrderLimitController extends Component {
  #abort;
  #pending;
  #revision = 0;
  #quantityTouchedAt = 0;
  #settleTimer;
  #settleResolve;
  #allowed = new WeakSet();
  #count = 0;
  #toastTimer;
  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
    document.addEventListener('submit', this.#onCheckout, { signal, capture: true });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
    this.#refresh();
  }
  disconnectedCallback() { super.disconnectedCallback(); this.#abort?.abort(); this.#revision++; clearTimeout(this.#settleTimer); this.#settleResolve?.(); clearTimeout(this.#toastTimer); }
  #readCart = async () => {
    const url = new URL(this.dataset.cartUrl, location.origin);
    url.searchParams.set('_anna_limit', Date.now());
    const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: AbortSignal.any([this.#abort.signal, AbortSignal.timeout(20000)]) });
    if (!response.ok) throw new Error('Cart check failed');
    const cart = await response.json();
    if (!Number.isInteger(cart.item_count)) throw new Error('Cart count missing');
    return cart;
  };
  #apply = (cart) => {
    this.#count = cart.item_count;
    if (cart.item_count < 5) { const toast = document.getElementById('anna-cart-limit-toast'); if (toast) toast.hidden = true; }
    document.querySelectorAll('anna-order-limit').forEach(banner => banner.updateCount(cart.item_count));
    document.querySelectorAll('button[name="checkout"]').forEach(button => {
      if (cart.item_count > 5) { button.classList.add('anna-limit-blocked'); button.disabled = true; }
      else if (button.classList.contains('anna-limit-blocked')) { button.classList.remove('anna-limit-blocked'); button.disabled = cart.item_count === 0; }
    });
  };
  #refresh = async () => {
    const revision = ++this.#revision;
    try { const cart = await this.#readCart(); if (revision === this.#revision && this.isConnected) this.#apply(cart); }
    catch(error) { if (revision === this.#revision && this.isConnected && error.name !== 'AbortError') document.querySelectorAll('anna-order-limit').forEach(banner => banner.showError()); }
  };
  #onPageShow = (event) => { if (event.persisted) this.#refresh(); };
  #onCartUpdate = (event) => {
    const pending = event.promise;
    this.#pending = pending;
    Promise.resolve(pending).catch(() => {}).then(async () => {
      if (this.#pending !== pending) return;
      this.#pending = null;
      await this.#refresh();
      const toast = document.getElementById('anna-cart-limit-toast');
      const drawer = document.querySelector('#cart-drawer dialog');
      if (!toast || this.#count < 5 || drawer?.open || document.body.classList.contains('template-cart') || location.pathname.endsWith('/cart')) return;
      const message = toast.querySelector('[data-limit-toast-message]');
      if (message) message.textContent = this.#count > 6 ? toast.dataset.overCopy.replace('[count]', String(this.#count - 5)) : this.#count === 6 ? toast.dataset.overOne : toast.dataset.fullCopy;
      toast.hidden = false;
      clearTimeout(this.#toastTimer);
      this.#toastTimer = setTimeout(() => { toast.hidden = true; }, 8000);
    });
  };
  #onQuantityUpdate = (event) => {
    if (event.detail?.cartLine) this.#quantityTouchedAt = Date.now();
  };
  #waitForCart = async () => {
    while (Date.now() - this.#quantityTouchedAt < 350 && this.isConnected) {
      await new Promise(resolve => {
        this.#settleResolve = resolve;
        this.#settleTimer = setTimeout(resolve, 350 - (Date.now() - this.#quantityTouchedAt));
      });
    }
    await this.#pending?.catch(() => {});
    while (CartItemsComponent.pendingQuantityUpdates.length && this.isConnected) {
      await Promise.allSettled(CartItemsComponent.pendingQuantityUpdates);
    }
  };
  #onCheckout = async (event) => {
    const form = event.target;
    const button = event.submitter;
    if (!(form instanceof HTMLFormElement) || !(button instanceof HTMLButtonElement) || button.name !== 'checkout') return;
    if (this.#allowed.has(form)) { this.#allowed.delete(form); return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (form.dataset.annaChecking) return;
    form.dataset.annaChecking = 'true';
    button.setAttribute('aria-busy', 'true');
    const label = button.querySelector('.button-text');
    const previousLabel = label?.textContent;
    if (label) label.textContent = this.dataset.checkingCopy;
    try {
      await this.#waitForCart();
      if (!this.isConnected) return;
      const cart = await this.#readCart();
      this.#apply(cart);
      if (cart.item_count > 0 && cart.item_count <= 5) {
        const currentForm = form.isConnected ? form : document.getElementById(form.id);
        const currentButton = button.isConnected ? button : currentForm?.elements.namedItem('checkout');
        if (currentForm instanceof HTMLFormElement && currentButton instanceof HTMLButtonElement) {
          this.#allowed.add(currentForm);
          currentForm.requestSubmit(currentButton);
        }
      }
      else form.closest('.cart-page, dialog')?.querySelector('anna-order-limit')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } catch(error) { if (error.name !== 'AbortError') document.querySelectorAll('anna-order-limit').forEach(banner => banner.showError()); }
    finally { delete form.dataset.annaChecking; button.removeAttribute('aria-busy'); if (label && previousLabel) label.textContent = previousLabel; }
  };
}
if (!customElements.get('anna-order-limit')) customElements.define('anna-order-limit', AnnaOrderLimit);
if (!customElements.get('anna-order-limit-controller')) customElements.define('anna-order-limit-controller', AnnaOrderLimitController);
